// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { areAddressesTheSame } from "@insite/client-framework/Common/CustomerSelectors";
import { makeHandlerChainAwaitable, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { createShipTo, updateShipTo } from "@insite/client-framework/Services/CustomersService";
import { FulfillmentMethod, Session, updateSession } from "@insite/client-framework/Services/SessionService";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import {
    AddOrUpdateShipTo,
    chain,
    UpdateSession,
} from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SaveShipping";
import { CustomAppState } from "@sc/App/Store/Reducers";

// XNG-Change
export const arePropertiesTheSame: any = (props: any) => {
    const state = props.getState();
    return (
        (state as CustomAppState).xCheckout.phone2 === props.parameter.phone2 &&
        (state as CustomAppState).xCheckout.smid === props.parameter.smid &&
        (state as CustomAppState).xCheckout.companyDepartmentNumber === props.parameter.companyDepartmentNumber
    );
};

export const ScAddOrUpdateShipTo: any = async (props: any) => {
    const { dispatch, getState, cart } = props;
    const state = getState();
    const {
        context: {
            session: { fulfillmentMethod },
        },
        pages: {
            onePageCheckout: { shippingAddressFormState, isBillingAddressTheSameAsShipping },
        },
    } = state;

    // XNG-Change: we can use one time address while the fulfillment method is pickup
    if (
        !shippingAddressFormState ||
        (fulfillmentMethod !== FulfillmentMethod.Ship && !props.parameter?.useOneTimeAddress)
    ) {
        return;
    }

    // XNG-Changes
    let addressProperties = {};
    if (props.parameter.phone2 && props.parameter.smid && props.parameter.companyDepartmentNumber) {
        addressProperties = {
            phone2: props.parameter.phone2,
            smid: props.parameter.smid,
            companyDepartmentNumber: props.parameter.companyDepartmentNumber,
        };
    }

    const shipTo = getShipToState(state, cart.shipToId).value;
    const billTo = getBillToState(state, cart.billToId).value;
    if (!billTo) {
        throw new Error("There was no billTo loaded for the current cart");
    }

    const { address: localModifiedShipTo } = shippingAddressFormState;
    if (isBillingAddressTheSameAsShipping && cart.shipToId !== cart.billToId && cart.billToId) {
        props.apiParameter.cart.shipToId = cart.billToId;
        const { shipToId } = props.apiParameter.cart;
        // XNG-Change: send properties in expand
        await makeHandlerChainAwaitable(loadShipTo)({
            shipToId,
            billToId: cart.billToId,
            expand: ["validation", "properties"] as any,
        })(dispatch, getState);
        const shipToState = getShipToState(getState(), shipToId);
        shipToState.value &&
            props.dispatch({
                type: "Pages/OnePageCheckout/SetShippingAddress",
                address: shipToState.value,
            });
        return;
    }

    let updatedShipTo;
    if (localModifiedShipTo.isNew || (!isBillingAddressTheSameAsShipping && cart.shipToId === cart.billToId)) {
        updatedShipTo = await createShipTo({
            shipTo: {
                ...localModifiedShipTo,
                properties: {
                    ...localModifiedShipTo.properties,
                    ...addressProperties,
                },
            },
        });
    }
    // XNG-Change: we need to update address if the new fields in properties were changed
    else if (shipTo && (!areAddressesTheSame(shipTo, localModifiedShipTo) || !arePropertiesTheSame(props))) {
        updatedShipTo = await updateShipTo({
            shipTo: {
                ...localModifiedShipTo,
                properties: {
                    ...localModifiedShipTo.properties,
                    ...addressProperties,
                },
            },
            billToId: billTo.id,
        });
    }

    if (updatedShipTo) {
        // XNG-Change
        props.dispatch({
            type: "xCheckout/SetOneTimeAddressExtraFields",
            phone2: props.parameter.phone2,
            smid: props.parameter.smid,
            companyDepartmentNumber: props.parameter.companyDepartmentNumber,
        });

        props.updatedShipTo = {
            ...updatedShipTo,
            validation: localModifiedShipTo.oneTimeAddress
                ? localModifiedShipTo.validation
                : (shipTo || updatedShipTo).validation,
        };
        // XNG-Change: send properties
        dispatch(loadShipTo({ shipToId: props.updatedShipTo.id, billToId: billTo.id, expand: ["properties"] as any }));
        props.dispatch({
            type: "Pages/OnePageCheckout/SetShippingAddress",
            address: props.updatedShipTo,
        });
        props.apiParameter.cart.shipToId = props.updatedShipTo.id;
    }
};
export const ScUpdateSession: typeof UpdateSession = async props => {
    const { cartId } = props.getState().pages.onePageCheckout;
    const {
        updatedShipTo,
        updatedBillTo,
        apiParameter: { cart },
    } = props;

    if (!cartId && (updatedShipTo || updatedBillTo)) {
        // XNG-Change: update the session only if the shipto is not onetime
        await updateSession({
            session: {
                billToId: updatedBillTo?.id ?? cart.billToId,
                customerWasUpdated: true,
                ...(!updatedShipTo?.oneTimeAddress && { shipToId: updatedShipTo?.id ?? cart.shipToId }),
            } as Session,
        });
    }
};

replaceInChain(chain, AddOrUpdateShipTo, ScAddOrUpdateShipTo);
replaceInChain(chain, UpdateSession, ScUpdateSession);
